import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Pencil, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { Field } from "./fields";
import { COUNTRIES } from "./countries";

import { PublicLinkButton } from "./FormLink";

export function calcAge(iso) {
  const b = new Date(iso);
  if (!iso || isNaN(b.getTime())) return "";
  const n = new Date();
  let a = n.getFullYear() - b.getFullYear();
  if (n.getMonth() < b.getMonth() || (n.getMonth() === b.getMonth() && n.getDate() < b.getDate())) a -= 1;
  return a >= 0 ? a : "";
}


export function RiskBadge({ level, t }) {
  const cls = { bajo: "bg-emerald-50 text-emerald-700", medio: "bg-amber-50 text-amber-700", alto: "bg-red-50 text-red-700" }[level] || "bg-slate-100 text-slate-600";
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`} data-testid={`risk-badge-${level}`}>{t(`k.level.${level}`)}</span>;
}

export function VerifyBadge({ verification, t }) {
  if (!verification) return <span className="text-xs text-slate-400">—</span>;
  const match = verification.result === "match";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${match ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
      {match ? `${t("k.verified_match")} (${verification.match_count})` : t("k.verified_clean")}
      {verification.pep_found && <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 rounded">{t("k.pep_flag")}</span>}
    </span>
  );
}

export default function SubjectRegistry({ kind, titleKey, subKey, addKey, fields, testid }) {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);

  const sendExpEmail = async () => {
    if (!detail) return;
    setEmailBusy(true);
    try {
      const { data } = await api.post(`/compliance/expedientes/${detail.id}/email`, {});
      toast.success(`${t("search.email_sent")} ${data.to}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEmailBusy(false);
    }
  };

  const delExp = async () => {
    if (!detail || !window.confirm(t("k.delete_confirm"))) return;
    try {
      await api.delete(`/compliance/expedientes/${detail.id}`);
      toast.success(t("k.deleted"));
      setDetail(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const [editId, setEditId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [att, setAtt] = useState({ filename: "", note: "", file_base64: null });
  const [attCreate, setAttCreate] = useState({ filename: "", file_base64: null });
  const [kyeFiles, setKyeFiles] = useState({ photo: null, police: null });

  const onKyeFile = (key) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return setKyeFiles((p) => ({ ...p, [key]: null }));
    if (f.size > 1_500_000) return toast.error(t("k.file_optional"));
    const rd = new FileReader();
    rd.onload = () => setKyeFiles((p) => ({ ...p, [key]: { filename: f.name, file_base64: rd.result } }));
    rd.readAsDataURL(f);
  };

  const onCreateFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setAttCreate({ filename: "", file_base64: null });
    if (f.size > 1_500_000) return toast.error(t("k.file_optional"));
    const rd = new FileReader();
    rd.onload = () => setAttCreate({ filename: f.name, file_base64: rd.result });
    rd.readAsDataURL(f);
  };

  const load = () => api.get("/compliance/expedientes", { params: { kind, ...(showInactive ? { include_inactive: "1" } : {}) } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [kind, showInactive]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/compliance/expedientes/${editId}`, { data: form });
        toast.success(t("u.updated"));
      } else {
        const atts = attCreate.file_base64 ? [{ filename: attCreate.filename, file_base64: attCreate.file_base64 }] : [];
        if (kind === "employee") {
          if (kyeFiles.photo) atts.unshift({ ...kyeFiles.photo, note: "Foto" });
          if (kyeFiles.police) atts.push({ ...kyeFiles.police, note: "Récord Policivo" });
        }
        await api.post("/compliance/expedientes", { kind, data: form, attachments: atts });
        toast.success(t("k.added"));
      }
      setShowForm(false);
      setEditId(null);
      setForm({});
      setAttCreate({ filename: "", file_base64: null });
      setKyeFiles({ photo: null, police: null });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reverify = async (id) => {
    try {
      await api.post(`/compliance/expedientes/${id}/reverify`);
      toast.success(t("k.reverified"));
      load();
      if (detail?.id === id) openDetail(id);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.patch(`/compliance/expedientes/${it.id}`, { data: {}, active: !(it.active !== false) });
      toast.success(t("u.updated"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const startEdit = () => {
    setForm({ ...(detail.data || {}) });
    setEditId(detail.id);
    setShowForm(true);
    setDetail(null);
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/compliance/expedientes/${id}`);
    setDetail(data);
  };

  const addAttachment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/compliance/expedientes/${detail.id}/attachments`, att);
      toast.success(t("k.att_added"));
      setAtt({ filename: "", note: "", file_base64: null });
      openDetail(detail.id);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setAtt({ ...att, file_base64: null });
    if (f.size > 1_500_000) return toast.error(t("k.file_optional"));
    const rd = new FileReader();
    rd.onload = () => setAtt((p) => ({ ...p, file_base64: rd.result, filename: p.filename || f.name }));
    rd.readAsDataURL(f);
  };

  return (
    <div className="space-y-6" data-testid={`${testid}-page`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t(titleKey)}</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">{t(subKey)}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadFile(`/export/compliance.pdf?kind=expedientes_${kind}&lang=${lang}`, `expedientes_${kind}_kycfederal.pdf`)} data-testid={`${testid}-export-pdf`} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#033A8C] text-white text-sm font-bold hover:bg-[#16335F]">
            <Download className="w-4 h-4" /> {t("common.export_pdf")}
          </button>
          <button onClick={() => downloadFile("/export/compliance.xlsx?kind=expedientes", "expedientes_kycfederal.xlsx")} data-testid={`${testid}-export-excel`} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800">
            <FileSpreadsheet className="w-4 h-4" /> {t("common.download_excel")}
          </button>
          {kind === "employee" && <PublicLinkButton formType="kye_employee" testid="kye-public-link" />}
          {kind === "supplier" && <PublicLinkButton formType="kyp_supplier" testid="kyp-public-link" />}
          {addKey && (
            <button onClick={() => setShowForm(!showForm)} data-testid={`${testid}-add-button`} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C]">
              <Plus className="w-4 h-4" /> {t(addKey)}
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="ec-card p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end" data-testid={`${testid}-form`}>
          {fields.map((f) => {
            if (f.reqIfAllergic && form.allergic !== true) return null;
            const req = !editId && Boolean(f.required || f.reqIfAllergic);
            return (
            <Field key={f.key} label={`${t(f.label)}${req ? " *" : ""}`}>
              {f.type === "country" ? (
                <select className="ec-input" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={req} data-testid={`${testid}-field-${f.key}`}>
                  <option value="">—</option>
                  {COUNTRIES.map(([es, en]) => <option key={en} value={lang === "en" ? en : es}>{lang === "en" ? en : es}</option>)}
                </select>
              ) : f.type === "select" ? (
                <select className="ec-input" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={req} data-testid={`${testid}-field-${f.key}`}>
                  <option value="">—</option>
                  {f.options.map((o) => {
                    const v = typeof o === "string" ? o : o.v;
                    return <option key={v} value={v}>{typeof o === "string" ? o : t(o.labelKey)}</option>;
                  })}
                </select>
              ) : f.type === "yesno" ? (
                <select className="ec-input" value={form[f.key] ?? "no"} onChange={(e) => setForm({ ...form, [f.key]: e.target.value === "si" })} data-testid={`${testid}-field-${f.key}`}>
                  <option value="no">{t("common.no")}</option>
                  <option value="si">{t("common.yes")}</option>
                </select>
              ) : (
                <>
                  <input className="ec-input" type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={req} data-testid={`${testid}-field-${f.key}`} />
                  {f.hint && form[f.key] ? <div className="text-xs text-slate-500 mt-1" data-testid={`${testid}-hint-${f.key}`}>{f.hint(form, t)}</div> : null}
                </>
              )}
            </Field>
            );
          })}
          {kind === "employee" && !editId && (
            <>
              <Field label={`${t("e.photo")} *`}>
                <div className="flex items-center gap-2">
                  <input className="ec-input" type="file" accept="image/*" required onChange={onKyeFile("photo")} data-testid={`${testid}-photo-file`} />
                  {kyeFiles.photo && <span className="text-xs font-semibold text-emerald-700 shrink-0">{kyeFiles.photo.filename}</span>}
                </div>
              </Field>
              <Field label={`${t("e.police_record")} *`}>
                <div className="flex items-center gap-2">
                  <input className="ec-input" type="file" required onChange={onKyeFile("police")} data-testid={`${testid}-police-file`} />
                  {kyeFiles.police && <span className="text-xs font-semibold text-emerald-700 shrink-0">{kyeFiles.police.filename}</span>}
                </div>
              </Field>
            </>
          )}
          <Field label={t("k.attachments")}>
            <div className="flex items-center gap-2">
              <input className="ec-input" type="file" onChange={onCreateFile} data-testid={`${testid}-attach-file`} />
              {attCreate.filename && <span className="text-xs font-semibold text-emerald-700 shrink-0">{attCreate.filename}</span>}
            </div>
          </Field>
          <button type="submit" disabled={busy} data-testid={`${testid}-submit`} className="px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("common.save")}
          </button>
        </form>
      )}

      <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 cursor-pointer">
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid={`${testid}-show-inactive`} />
        {t("common.show_inactive")}
      </label>

      <div className="ec-card overflow-hidden" data-testid={`${testid}-table`}>
        {items === null ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid={`${testid}-empty`}>{t("h.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("h.name_title")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">ID</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("k.level")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("k.dd_level")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("k.last_verify")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("k.next_review")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${it.active === false ? "opacity-50" : ""}`} onClick={() => openDetail(it.id)} data-testid={`${testid}-row-${it.id}`}>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">
                      {it.name}
                      {it.active === false && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">{t("common.inactive")}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs font-mono">
                      {it.id_number || "—"}
                      {it.client_id && <div className="text-[10px] text-[#033A8C]">{it.client_id}</div>}
                    </td>
                    <td className="px-4 py-2.5"><RiskBadge level={it.risk?.level} t={t} /></td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{t(`k.dd.${it.risk?.dd_level}`)}</td>
                    <td className="px-4 py-2.5"><VerifyBadge verification={it.verification} t={t} /><div className="text-[10px] text-slate-400 mt-0.5">{fmtDate(it.verification?.date)}</div></td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{it.next_review || "—"}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button onClick={(e) => { e.stopPropagation(); toggleActive(it); }} data-testid={`${testid}-toggle-${it.id}`} className={`text-xs font-bold mr-3 ${it.active !== false ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800"}`}>
                        {it.active !== false ? t("common.deactivate") : t("common.activate")}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); reverify(it.id); }} data-testid={`${testid}-reverify-${it.id}`} className="text-[#044CBC] hover:text-[#033A8C] text-xs font-bold inline-flex items-center gap-1">
                        <RefreshCw className="w-3.5 h-3.5" /> {t("k.reverify")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="ec-card max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-5" onClick={(e) => e.stopPropagation()} data-testid="expediente-detail">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-extrabold text-slate-900">{detail.name}</h3>
                {detail.client_id && <div className="text-xs font-mono text-[#033A8C]">{detail.client_id}</div>}
                <div className="text-xs text-slate-500">{t("k.last_update")}: {fmtDate(detail.updated_at)} · {t("k.next_review")}: {detail.next_review}</div>
              </div>
              <div className="flex items-center gap-3">
                <RiskBadge level={detail.risk?.level} t={t} />
                <button onClick={() => downloadFile(`/compliance/expedientes/${detail.id}/pdf?lang=${lang}`, `expediente_${detail.client_id || detail.id}.pdf`)} data-testid="expediente-pdf-button" className="text-xs font-bold text-[#033A8C] hover:text-[#044CBC] inline-flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> {t("search.pdf")}
                </button>
                <button onClick={sendExpEmail} disabled={emailBusy} data-testid="expediente-email-button" className="text-xs font-bold text-[#033A8C] hover:text-[#044CBC] inline-flex items-center gap-1 disabled:opacity-60">
                  {emailBusy ? "… " : ""}{t("search.send_email")}
                </button>
                <button onClick={startEdit} data-testid="expediente-edit-button" className="text-xs font-bold text-[#033A8C] hover:text-[#044CBC] inline-flex items-center gap-1">
                  <Pencil className="w-3.5 h-3.5" /> {t("common.edit")}
                </button>
                <button onClick={delExp} data-testid="expediente-delete-button" className="text-xs font-bold text-red-500 hover:text-red-700 inline-flex items-center gap-1">
                  {t("common.delete")}
                </button>
              </div>
            </div>
            <div>
              <h4 className="ec-label">{t("k.factors")}</h4>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500 uppercase"><th className="py-1">{t("k.factor")}</th><th>{t("k.value")}</th><th className="text-right">{t("k.score")}</th></tr></thead>
                <tbody>
                  {(detail.risk?.factors || []).map((f) => (
                    <tr key={f.key} className="border-t border-slate-100" data-testid={`factor-${f.key}`}>
                      <td className="py-1.5 text-slate-700">{FACTOR_LABELS_LOCAL(lang, f.key)}</td>
                      <td className="py-1.5 text-slate-500 text-xs">{f.value}</td>
                      <td className="py-1.5 text-right font-mono font-bold">{f.score}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 font-bold"><td className="py-1.5">{t("k.total")}</td><td /><td className="text-right font-mono">{detail.risk?.total}</td></tr>
                </tbody>
              </table>
              <div className="mt-2 text-sm">{t("k.dd_level")}: <span className="font-bold text-[#033A8C]">{t(`k.dd.${detail.risk?.dd_level}`)}</span></div>
            </div>
            <div>
              <h4 className="ec-label">{t("k.last_verify")}</h4>
              <VerifyBadge verification={detail.verification} t={t} />
              {detail.verification?.top_matches?.length > 0 && (
                <ul className="mt-2 text-xs text-slate-600 space-y-0.5">
                  {detail.verification.top_matches.map((m, i) => <li key={i}>· [{m.source}] {m.name} — {m.score}%</li>)}
                </ul>
              )}
            </div>
            <div>
            {(detail.attachments || []).some((a) => a.report_kind || (a.filename || "").startsWith("osint_")) && (
                <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid="exp-osint-summary">
                  <h4 className="ec-label !text-emerald-800">{t("k.intel_summary")}</h4>
                  <ul className="space-y-1 text-xs text-slate-700">
                    {(detail.attachments || []).filter((a) => a.report_kind || (a.filename || "").startsWith("osint_")).map((a) => {
                      const kind = a.report_kind || "osint";
                      return (
                      <li key={a.id} className="flex items-center justify-between gap-2" data-testid={`exp-osint-item-${a.id}`}>
                        <span><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mr-1.5 ${kind === "kev" ? "bg-red-100 text-red-800" : kind === "ntas" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`exp-report-kind-${a.id}`}>{{ osint: "OSINT", kev: "CISA KEV", ntas: "NTAS" }[kind]}</span><span className="font-semibold">{a.note || a.filename}</span> — {a.uploaded_by} · {fmtDate(a.date)}</span>
                        <button onClick={() => downloadFile(`/compliance/expedientes/${detail.id}/attachments/${a.id}/download`, a.filename)} className="text-[#044CBC] hover:text-[#033A8C]" data-testid={`exp-osint-dl-${a.id}`}><Download className="w-3.5 h-3.5" /></button>
                      </li>);
                    })}
                  </ul>
                </div>
              )}
              <h4 className="ec-label">{t("k.attachments")}</h4>
              {(detail.attachments || []).length === 0 && <p className="text-xs text-slate-400">{t("k.no_attachments")}</p>}
              <ul className="space-y-1.5">
                {(detail.attachments || []).map((a) => (
                  <li key={a.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2.5 py-1.5" data-testid={`attachment-${a.id}`}>
                    <span className="font-semibold text-slate-700">{a.filename} <span className="font-normal text-slate-400">— {a.note} · {a.uploaded_by} · {fmtDate(a.date)}</span></span>
                    <button onClick={() => downloadFile(`/compliance/expedientes/${detail.id}/attachments/${a.id}/download`, a.filename)} className="text-[#044CBC] hover:text-[#033A8C]" data-testid={`attachment-dl-${a.id}`}><Download className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
              <form onSubmit={addAttachment} className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <input className="ec-input" placeholder={t("k.filename")} value={att.filename} onChange={(e) => setAtt({ ...att, filename: e.target.value })} required data-testid="attachment-filename" />
                <input className="ec-input" type="file" onChange={onFile} data-testid="attachment-file" />
                <button type="submit" className="px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold" data-testid="attachment-submit">{t("k.add_attachment")}</button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FACTOR_LABELS_LOCAL(lang, key) {
  const L = {
    client: { es: "Factor cliente", en: "Client factor" },
    product: { es: "Factor producto o servicio", en: "Product or service factor" },
    channel: { es: "Factor canal de distribución", en: "Distribution channel factor" },
    geography: { es: "Factor zona geográfica", en: "Geographic zone factor" },
    country: { es: "Factor país / nacionalidad", en: "Country / nationality factor" },
    pep: { es: "Factor PEP", en: "PEP factor" },
    activity: { es: "Factor actividad / sector", en: "Activity / sector factor" },
  };
  return L[key]?.[lang] || key;
}
